import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBarcode, FaBox, FaBoxOpen, FaCalendarAlt, FaCamera, FaChartLine, FaCog, FaEdit, FaEnvelope, FaEye, FaEyeSlash, FaFileDownload, FaFileUpload, FaList, FaMapMarkerAlt, FaMoneyBill, FaMoneyBillWave, FaPlus, FaPrint, FaQrcode, FaReceipt, FaSearch, FaStore, FaTag, FaTicketAlt, FaTimes, FaTrash, FaUndoAlt, FaUser, FaWhatsapp } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Pagination, PaginationItem, PaginationLink, Row, Table, Toast, ToastBody, ToastHeader } from 'reactstrap';
import Swal from 'sweetalert2';
import { authActions } from '../../sagas/authSlice';

import config from '../../helpers/config';
const ProfilePage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { company } = useSelector(state => state.auth);
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [isCompanyInfoEditable, setIsCompanyInfoEditable] = useState(false);
    const [logoPreview, setLogoPreview] = useState(company?.logo || null);
    const [logoFile, setLogoFile] = useState(null);

    const [companyInfo, setCompanyInfo] = useState({
        name: company.name || '',
        subtitle: company?.subtitle || '',
        address: company?.address || '',
        phone1: company?.phone1 || '',
        phone2: company?.phone2 || '',
        email: company?.email || '',
        identificationFiscale: company?.identificationFiscale || '',
        ice: company?.ice || '',
        patente: company?.patente || '',
        rc: company?.rc || '',
        rib: company?.rib || '',
        cnss: company?.cnss || '',
    });

    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [error, setError] = useState('');

    useEffect(() => { }, [company]);

    const handleCompanyInfoChange = (e) => {
        const { name, value } = e.target;
        setCompanyInfo(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleToggleCompanyInfoEdit = () => {
        setIsCompanyInfoEditable(!isCompanyInfoEditable);
        if (!isCompanyInfoEditable) {
            setCompanyInfo({
                name: company?.name || '',
                subtitle: company?.subtitle || '',
                address: company?.address || '',
                phone1: company?.phone1 || '',
                phone2: company?.phone2 || '',
                email: company?.email || '',
                identificationFiscale: company?.identificationFiscale || '',
                ice: company?.ice || '',
                patente: company?.patente || '',
                rib: company.rib || '',
                rc: company?.rc || '',
                cnss: company?.cnss || ''
            });

            setLogoFile(null);
            setLogoPreview(company?.logo || null);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setError(t('profile.passwordMismatch'));
            return;
        }
        try {
            dispatch(authActions.changePassword({
                form: passwordForm, onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('profile.passwordChanged'),
                        showConfirmButton: false,
                        timer: 1500
                    });
                    setPasswordForm({
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: ''
                    });
                },
                onError: (error) => {
                    Swal.fire({
                        icon: 'error',
                        title: t('profile.changePasswordError'),
                    });
                }
            }));
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: t('common.error'),
                text: error.message
            });
        }
    };

    const handleSaveCompanyInfo = () => {
        const formData = new FormData();
        formData.append('company', JSON.stringify(companyInfo));
        if (logoFile) {
            formData.append('logo', logoFile);
        }

        dispatch(authActions.updateCompanyInfo({
            companyInfo: formData,
            onSuccess: () => {
                Swal.fire({
                    icon: 'success',
                    title: t('profile.companyInfoUpdateSuccess'),
                    showConfirmButton: false,
                    timer: 1500
                });
                setIsCompanyInfoEditable(false);
            },
            onError: () => {
                Swal.fire({
                    icon: 'error',
                    title: t('profile.companyInfoUpdateError')
                });
            }
        }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleClearLogo = () => {
        setLogoFile(null);
        setLogoPreview(null);
    };


    return (
        <Container fluid>
            <Row className="mb-3">
                <Col>
                    <Card className="shadow-sm border-0">
                        <CardBody className="py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h4 className="mb-0 d-flex align-items-center">
                                    <FaCog className="text-primary me-2" />
                                    {t('profile.pageTitle')}
                                </h4>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col md={8}>
                    <Card className="shadow-sm">
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">{t('profile.businessInfo')}</h5>
                                <Button color="link" onClick={handleToggleCompanyInfoEdit}>
                                    {isCompanyInfoEditable ? t('common.cancel') : t('common.edit')}
                                </Button>
                            </div>
                            <Form>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>{t('profile.businessName')}</Label>
                                            <Input
                                                type="text"
                                                name="name"
                                                value={companyInfo.name}
                                                onChange={handleCompanyInfoChange}
                                                disabled={!isCompanyInfoEditable}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>{t('profile.activite')}</Label>
                                            <Input
                                                type="text"
                                                name="subtitle"
                                                value={companyInfo.subtitle}
                                                onChange={handleCompanyInfoChange}
                                                disabled={!isCompanyInfoEditable}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>{t('profile.address')}</Label>
                                            <Input
                                                type="textarea"
                                                rows={3}
                                                name="address"
                                                value={companyInfo.address}
                                                onChange={handleCompanyInfoChange}
                                                disabled={!isCompanyInfoEditable}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>{t('profile.telephone1')}</Label>
                                            <Input
                                                type="text"
                                                name="phone1"
                                                value={companyInfo.phone1}
                                                onChange={handleCompanyInfoChange}
                                                disabled={!isCompanyInfoEditable}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>{t('profile.telephone2')}</Label>
                                            <Input
                                                type="text"
                                                name="phone2"
                                                value={companyInfo.phone2}
                                                onChange={handleCompanyInfoChange}
                                                disabled={!isCompanyInfoEditable}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>{t('profile.email')}</Label>
                                            <Input
                                                type="email"
                                                name="email"
                                                value={companyInfo.email}
                                                onChange={handleCompanyInfoChange}
                                                disabled={!isCompanyInfoEditable}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>{t('profile.rib')}</Label>
                                            <Input
                                                type="text"
                                                name="rib"
                                                value={companyInfo.rib}
                                                onChange={handleCompanyInfoChange}
                                                disabled={!isCompanyInfoEditable}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>{t('profile.identificationFiscale')}</Label>
                                            <Input
                                                type="text"
                                                name="identificationFiscale"
                                                value={companyInfo.identificationFiscale}
                                                onChange={handleCompanyInfoChange}
                                                disabled={!isCompanyInfoEditable}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>{t('profile.ice')}</Label>
                                            <Input
                                                type="text"
                                                name="ice"
                                                value={companyInfo.ice}
                                                onChange={handleCompanyInfoChange}
                                                disabled={!isCompanyInfoEditable}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>{t('profile.patente')}</Label>
                                            <Input
                                                type="text"
                                                name="patente"
                                                value={companyInfo.patente}
                                                onChange={handleCompanyInfoChange}
                                                disabled={!isCompanyInfoEditable}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>{t('profile.rc')}</Label>
                                            <Input
                                                type="text"
                                                name="rc"
                                                value={companyInfo.rc}
                                                onChange={handleCompanyInfoChange}
                                                disabled={!isCompanyInfoEditable}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>{t('profile.cnss')}</Label>
                                            <Input
                                                type="text"
                                                name="cnss"
                                                value={companyInfo.cnss}
                                                onChange={handleCompanyInfoChange}
                                                disabled={!isCompanyInfoEditable}
                                            />
                                        </FormGroup>
                                    </Col>
                                    {/* <Col md={4}>
                                        <FormGroup className="mt-3">
                                            <Label>{t('profile.receiptLanguage')}</Label>
                                            <div className="d-flex align-items-center">
                                                <div className="btn-group">
                                                    <Button
                                                        color={companyInfo.receiptLanguage === 'fr' ? 'primary' : 'light'}
                                                        onClick={() => handleReceiptLanguageChange('fr')}
                                                    >
                                                        {t('language.french')}
                                                    </Button>
                                                    <Button
                                                        color={companyInfo.receiptLanguage === 'ar' ? 'primary' : 'light'}
                                                        onClick={() => handleReceiptLanguageChange('ar')}
                                                    >
                                                        {t('language.arabic')}
                                                    </Button>
                                                </div>
                                            </div>
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>{t('profile.model')}</Label>
                                            <Input
                                                type="select"
                                                name="modelInvoice"
                                                value={companyInfo.modelInvoice}
                                                onChange={handleCompanyInfoChange}
                                                disabled={!isCompanyInfoEditable}
                                            >
                                                {models.map(model => (
                                                    <option key={model.value} value={model.value}>{model.label}</option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                    </Col> */}
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label>{t('profile.logo')}</Label>
                                            <div className="d-flex align-items-center gap-3">
                                                {logoPreview ? (
                                                    <div className="position-relative">
                                                        <img
                                                            src={logoPreview ? (logoPreview.startsWith("/uploads") ? `${config.baseURL}${logoPreview}` : logoPreview) : null}
                                                            alt="Logo Preview"
                                                            style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                                            className="border rounded"
                                                        />
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            className="position-absolute top-0 end-0 rounded-circle"
                                                            onClick={handleClearLogo}
                                                            disabled={!isCompanyInfoEditable}
                                                        >
                                                            <FaTimes />
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="border rounded d-flex align-items-center justify-content-center"
                                                        style={{ width: '100px', height: '100px', cursor: 'pointer' }}
                                                        onClick={() => {
                                                            if (isCompanyInfoEditable) {
                                                                document.getElementById('companyLogo').click()
                                                            }
                                                        }}
                                                    >
                                                        <FaCamera size={24} className="text-muted" />
                                                    </div>
                                                )}
                                                <Input
                                                    type="file"
                                                    id="companyLogo"
                                                    accept="image/*"
                                                    onChange={handleLogoChange}
                                                    style={{ display: 'none' }}
                                                    disabled={!isCompanyInfoEditable}
                                                />
                                            </div>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                {/* <Row>
                                    <Col md={12}>
                                        <Label>{t('profile.modelPreview')}</Label>
                                        {isCompanyInfoEditable ? (
                                            <img
                                                src={models.find(model => model.value === companyInfo.modelInvoice)?.image}
                                                alt="Model Preview"
                                                style={{ maxWidth: '100%', height: 'auto' }}
                                            />
                                        ) : (
                                            <img
                                                src={models.find(model => model.value === company.modelInvoice)?.image}
                                                alt="Model Preview"
                                                style={{ maxWidth: '100%', height: 'auto' }}
                                            />
                                        )}
                                    </Col>
                                </Row> */}
                                {isCompanyInfoEditable && (
                                    <Button color="primary" onClick={handleSaveCompanyInfo}>
                                        {t('common.save')}
                                    </Button>
                                )}
                            </Form>

                        </CardBody>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm">
                        <CardBody>
                            <h5 className="mb-4">{t('profile.changePassword')}</h5>
                            <Form onSubmit={handlePasswordChange}>
                                <FormGroup>
                                    <Label>{t('profile.currentPassword')}</Label>
                                    <div className="position-relative">
                                        <Input
                                            type={showPasswords.current ? "text" : "password"}
                                            value={passwordForm.currentPassword}
                                            onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                                            required
                                        />
                                        <Button
                                            type="button"
                                            color="link"
                                            className="position-absolute end-0 top-50 translate-middle-y border-0"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowPasswords({ ...showPasswords, current: !showPasswords.current })
                                                e.currentTarget.blur();
                                            }}
                                            tabIndex={-1}
                                        >
                                            {showPasswords.current ? <FaEye /> : <FaEyeSlash />}
                                        </Button>
                                    </div>
                                </FormGroup>
                                <FormGroup>
                                    <Label>{t('profile.newPassword')}</Label>
                                    <div className="position-relative">
                                        <Input
                                            type={showPasswords.new ? "text" : "password"}
                                            value={passwordForm.newPassword}
                                            onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                                            required
                                        />
                                        <Button
                                            type="button"
                                            color="link"
                                            className="position-absolute end-0 top-50 translate-middle-y border-0"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowPasswords({ ...showPasswords, new: !showPasswords.new })
                                                e.currentTarget.blur();
                                            }}
                                            tabIndex={-1}
                                        >
                                            {showPasswords.new ? <FaEye /> : <FaEyeSlash />}
                                        </Button>
                                    </div>
                                </FormGroup>
                                <FormGroup>
                                    <Label>{t('profile.confirmPassword')}</Label>
                                    <div className="position-relative">
                                        <Input
                                            type={showPasswords.confirm ? "text" : "password"}
                                            value={passwordForm.confirmPassword}
                                            onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                                            required
                                        />
                                        <Button
                                            type="button"
                                            color="link"
                                            className="position-absolute end-0 top-50 translate-middle-y border-0"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })
                                                e.currentTarget.blur();
                                            }}
                                            tabIndex={-1}
                                        >
                                            {showPasswords.confirm ? <FaEye /> : <FaEyeSlash />}
                                        </Button>
                                    </div>
                                </FormGroup>

                                {error && <div className="text-danger mb-3">{error}</div>}
                                <Button color="primary" type="submit">
                                    {t('common.save')}
                                </Button>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default ProfilePage;